import { mobile } from '@bilibili-maoer/web-utils'

import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { z } from 'zod'

import { Pixels } from '~/shared/types/base/units'
import pipe from '~/shared/utils/pipe'

export type HeaderType = 'App' | 'Web'

export type SidebarState = 'Collapsed' | 'Expanded'

export type ReturnButtonBehavior = 'Backup' | 'Close'

export interface UiState {
  headerType: HeaderType
  returnButtonBehavior: ReturnButtonBehavior
  sidebarState: SidebarState
  navHide: boolean
  safeAreaInset?: {
    top: Pixels
    bottom: Pixels
  }
}

/**
 * 解析 userAgent 中的 SafeArea 字段，获取安全区域信息。
 * 例如：
 *   (Theme Dark; NetType WiFi; SafeArea 40,20; Swimlane test)
 *   => { top: 40<Pixels>, bottom: 20<Pixels> }
 */
const parseSafeAreaInsetFromUserAgent = () =>
  pipe(
    window.navigator.userAgent,
    (ua) => {
      if (mobile.isAppOlderThan({ [mobile.EDevice.Android]: '6.0.6', [mobile.EDevice.IOS]: '6.0.6' })) {
        if (mobile.isIOS()) {
          const version = ua.match(/cpu iphone os (.*?) like mac os/i)
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          if (version?.[1] && parseInt(version[1]) > 13) {
            return { top: '59', bottom: '34' }
          }
          return { top: '24', bottom: '0' }
        }
        const notchHeight = ua.match(/isNotchWindow\/1 NotchHeight=(\d+)/)
        return { top: notchHeight?.[1] || '24', bottom: '0' }
      }
      return ua.match(/SafeArea (?<top>\d+),(?<bottom>\d+);?/)?.groups
    },
    (group) =>
      z
        .object({
          top: z.coerce.number().int(),
          bottom: z.coerce.number().int(),
        })
        .safeParse(group),
    (res) =>
      res.success
        ? {
            top: res.data.top as Pixels,
            bottom: res.data.bottom as Pixels,
          }
        : undefined
  )

const initialState: UiState = {
  headerType: mobile.isMissEvanApp() ? 'App' : 'Web',
  returnButtonBehavior: 'Backup',
  sidebarState: 'Collapsed',
  // TODO: 监听 URL 变化，自动更新 navHide
  navHide: mobile.isMissEvanApp() && window.location.search.includes('navhide=1'),
  safeAreaInset: parseSafeAreaInsetFromUserAgent(),
}

const UiSlice = createSlice({
  name: 'Ui',
  initialState,
  reducers: {
    setReturnButtonBehavior: (state, action: PayloadAction<ReturnButtonBehavior>) => {
      state.returnButtonBehavior = action.payload
    },

    expandSidebar: (state) => {
      state.sidebarState = 'Expanded'
    },
    collapseSidebar: (state) => {
      state.sidebarState = 'Collapsed'
    },
    toggleSidebar: (state) => {
      state.sidebarState = state.sidebarState === 'Collapsed' ? 'Expanded' : 'Collapsed'
    },
  },
})

export default UiSlice
